import { useState, useCallback } from 'react';
import { webhooksService } from '@/services/webhooksService';

/**
 * Custom hook for managing webhooks
 * @param {string} projectId - Project ID
 * @returns {Object} Webhooks state and operations
 */
export const useWebhooks = projectId => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  /**
   * Fetch webhooks from the server
   */
  const fetchWebhooks = useCallback(
    async (page = 1, limit = 10, isActive = null) => {
      if (!projectId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await webhooksService.getWebhooks(
          projectId,
          page,
          limit,
          isActive
        );

        if (response.success) {
          setWebhooks(response.data.webhooks);
          setPagination({
            page: response.data.page,
            limit: response.data.limit,
            total: response.data.total,
            pages: response.data.pages,
          });
        } else {
          setError(response.error?.message || 'Failed to fetch webhooks');
        }
      } catch (err) {
        console.error('Error fetching webhooks:', err);
        setError(err.message || 'Failed to fetch webhooks');
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  /**
   * Create a new webhook
   */
  const createWebhook = useCallback(
    async (name, url, events, isActive = true) => {
      if (!projectId) throw new Error('Project ID is required');

      setLoading(true);
      setError(null);

      try {
        const response = await webhooksService.createWebhook(
          projectId,
          name,
          url,
          events,
          isActive
        );

        if (response.success) {
          // Refresh the list to include the new webhook
          await fetchWebhooks(pagination.page, pagination.limit);
          return response.data.webhook;
        } else {
          throw new Error(
            response.error?.message || 'Failed to create webhook'
          );
        }
      } catch (err) {
        console.error('Error creating webhook:', err);
        setError(err.message || 'Failed to create webhook');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId, fetchWebhooks, pagination.page, pagination.limit]
  );

  /**
   * Update an existing webhook
   */
  const updateWebhook = useCallback(
    async (webhookId, name, url, events, isActive) => {
      if (!projectId) throw new Error('Project ID is required');

      setLoading(true);
      setError(null);

      try {
        const response = await webhooksService.updateWebhook(
          webhookId,
          projectId,
          name,
          url,
          events,
          isActive
        );

        if (response.success) {
          // Update the webhook in the local state
          setWebhooks(prev =>
            prev.map(webhook =>
              webhook.id === webhookId ? response.data.webhook : webhook
            )
          );
          return response.data.webhook;
        } else {
          throw new Error(
            response.error?.message || 'Failed to update webhook'
          );
        }
      } catch (err) {
        console.error('Error updating webhook:', err);
        setError(err.message || 'Failed to update webhook');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  /**
   * Delete a webhook
   */
  const deleteWebhook = useCallback(
    async webhookId => {
      if (!projectId) throw new Error('Project ID is required');

      setLoading(true);
      setError(null);

      try {
        const response = await webhooksService.deleteWebhook(
          webhookId,
          projectId
        );

        if (response.success) {
          // Remove the webhook from local state
          setWebhooks(prev => prev.filter(webhook => webhook.id !== webhookId));
          return response;
        } else {
          throw new Error(
            response.error?.message || 'Failed to delete webhook'
          );
        }
      } catch (err) {
        console.error('Error deleting webhook:', err);
        setError(err.message || 'Failed to delete webhook');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  /**
   * Test a webhook
   */
  const testWebhook = useCallback(
    async webhookId => {
      if (!projectId) throw new Error('Project ID is required');

      try {
        const response = await webhooksService.testWebhook(
          webhookId,
          projectId
        );

        if (response.success) {
          return response.data.testResult;
        } else {
          throw new Error(response.error?.message || 'Failed to test webhook');
        }
      } catch (err) {
        console.error('Error testing webhook:', err);
        throw err;
      }
    },
    [projectId]
  );

  /**
   * Get webhook analytics
   */
  const getWebhookAnalytics = useCallback(
    async (webhookId, startDate = null, endDate = null) => {
      if (!projectId) throw new Error('Project ID is required');

      try {
        const response = await webhooksService.getWebhookAnalytics(
          webhookId,
          projectId,
          startDate,
          endDate
        );

        if (response.success) {
          return response.data.analytics;
        } else {
          throw new Error(
            response.error?.message || 'Failed to fetch webhook analytics'
          );
        }
      } catch (err) {
        console.error('Error fetching webhook analytics:', err);
        throw err;
      }
    },
    [projectId]
  );

  /**
   * Get a specific webhook by ID
   */
  const getWebhookById = useCallback(
    async webhookId => {
      if (!projectId) throw new Error('Project ID is required');

      try {
        const response = await webhooksService.getWebhookById(
          webhookId,
          projectId
        );

        if (response.success) {
          return response.data.webhook;
        } else {
          throw new Error(response.error?.message || 'Failed to fetch webhook');
        }
      } catch (err) {
        console.error('Error fetching webhook:', err);
        throw err;
      }
    },
    [projectId]
  );

  /**
   * Get available webhook events
   */
  const getAvailableEvents = useCallback(() => {
    return webhooksService.getAvailableEvents();
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Load webhooks on mount and when projectId changes
   * Note: This is now disabled to allow manual loading on tab click
   */
  // useEffect(() => {
  //   if (projectId) {
  //     fetchWebhooks();
  //   }
  // }, [projectId, fetchWebhooks]);

  return {
    // State
    webhooks,
    loading,
    error,
    pagination,

    // Actions
    fetchWebhooks,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    testWebhook,
    getWebhookAnalytics,
    getWebhookById,
    getAvailableEvents,
    clearError,
  };
};
